import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Shield, Scale, Award, AlertCircle, Clock, DollarSign, FileText, Users, Heart, ChevronRight, BookOpen, ArrowRight, Calculator, Flame, Truck, HardHat, Wrench } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedPracticeAreas from '@/components/RelatedPracticeAreas';
import FirstResponderFAQ from '@/components/FirstResponderFAQ';
import PageVideoSection from '@/components/PageVideoSection';

// First responder / workplace videos
const firstResponderVideos = [
  {
    id: 'TUK7bXZABAc',
    title: "Workplace Crashes: Beyond Workers' Comp",
    description: "Why L&I may not cover all your damages and how to pursue additional compensation.",
    duration: "2:43"
  },
  {
    id: 'Je6VlRW7yJc',
    title: "Workers' Comp vs. Third-Party Claims",
    description: "Understanding your dual rights when injured on the job by a third party.",
    duration: "2:42"
  }
];

const firstResponderFAQs = [
  {
    question: "Can I sue someone if I was injured on duty and already getting workers' comp?",
    answer: "Yes! Workers' comp only covers a portion of your losses. If a third party (like a negligent driver) caused your injury, you can file a civil lawsuit against them for full damages including pain and suffering—which workers' comp doesn't cover."
  },
  {
    question: "I'm a firefighter or paramedic—can I also pursue a civil claim?",
    answer: "Absolutely. Firefighters, paramedics, and EMTs injured by a third party's negligence (like a driver who hits your ambulance or fire truck) can file civil claims just like law enforcement. The key is that someone other than your employer caused the injury."
  },
  {
    question: "I work for WSDOT and was hit while working on the highway. Do I have a claim?",
    answer: "Yes. WSDOT workers, flaggers, and highway maintenance crews are frequently struck by negligent drivers in work zones. While workers' comp covers some losses, a civil claim against the at-fault driver can recover pain and suffering, full lost wages, and future medical needs."
  },
  {
    question: "I'm a city or county public works employee. Can I pursue a third-party claim?",
    answer: "Yes. If you work for a county road department, city public works, or utility crew and are injured by someone else's negligence—such as a driver hitting you in a work zone—you likely have a civil claim beyond workers' compensation."
  },
  {
    question: "What's the difference between workers' comp and a third-party claim?",
    answer: "Workers' comp provides limited medical and wage benefits regardless of fault. A third-party civil claim lets you sue the person who caused your injury for full compensation including pain and suffering, future earning capacity, and complete lost wages."
  },
  {
    question: "How long do I have to file a claim as an injured public safety worker?",
    answer: "Washington's statute of limitations is typically 3 years for personal injury claims. However, claims against government entities may have shorter notice requirements. Contact me promptly to ensure you don't miss any deadlines."
  },
  {
    question: "Will filing a lawsuit affect my job or pension?",
    answer: "No. Your right to sue a negligent third party is separate from your employment. Many public safety workers successfully pursue civil claims while continuing their careers or during disability leave."
  }
];

const FirstResponderPage = () => {
  // HowTo schema for "Filing a Third-Party Claim as a Public Safety Worker"
  const howToSteps = {
    name: "How Public Safety Workers Can File a Third-Party Injury Claim in Washington",
    description: "Steps for first responders, WSDOT workers, and public works employees to pursue civil claims against negligent drivers beyond workers' compensation.",
    totalTime: "PT30M",
    steps: [
      { name: "Report the Incident to Your Employer", text: "Follow your department or agency's protocol for reporting on-duty injuries. Ensure all documentation is filed correctly for both workers' comp and potential civil claims." },
      { name: "Seek Comprehensive Medical Care", text: "Get thorough medical documentation of all injuries. Some injuries may qualify for workers' comp while others support a civil claim." },
      { name: "Identify the At-Fault Party", text: "Determine who caused your injury. If a civilian driver or third party was negligent, you may have a civil claim against them." },
      { name: "Preserve All Evidence", text: "Keep copies of police reports, dash cam footage, body cam video, witness statements, and any evidence of the other party's negligence." },
      { name: "Understand Your Dual Rights", text: "Know that workers' compensation and third-party civil claims are separate. You can pursue both simultaneously for different types of damages." },
      { name: "Contact a First Responder Injury Attorney", text: "Speak with an attorney experienced in police officer injury claims. I've secured six-figure settlements for officers injured by negligent drivers." },
      { name: "File Your Civil Lawsuit", text: "Your attorney will file a claim against the at-fault party's insurance, pursuing compensation for pain and suffering, full lost wages, and other damages workers' comp doesn't cover." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="first-responder-page">
      <SEOHead 
        title="First Responder & Public Safety Worker Injury Attorney | Washington State | Amy C. Brown"
        description="Injury attorney for police officers, firefighters, paramedics, EMTs, WSDOT workers, and public works employees in Washington. Third-party civil claims beyond workers' comp. Free consultation."
        serviceData={{
          name: "Public Safety Worker Injury Legal Representation",
          description: "Legal representation for first responders and public safety workers injured on the job, including police officers, firefighters, paramedics, EMTs, WSDOT highway workers, and county/city public works employees. Third-party civil claims against negligent drivers for full compensation beyond workers' compensation.",
          category: "Public Safety Worker Law"
        }}
        howToData={howToSteps}
        speakableSelectors={['h1', 'h2', '[data-testid="who-i-help-section"] p']}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              Washington State
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              First Responders & Public Safety Workers
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              If you're a <strong className="text-secondary">law enforcement officer, firefighter, paramedic, EMT, WSDOT worker, or public works employee</strong> injured on the job by someone else's negligence, you may have a civil claim beyond workers' compensation. Many public safety workers assume they're limited to workers' comp benefits. <strong className="text-secondary">This is not accurate.</strong> When a third party—like a negligent driver—causes your injury, you can pursue a civil lawsuit for full compensation including pain and suffering. I have pursued these cases and won.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="first-responder-hero-call"
              >
                <Phone className="w-5 h-5" />
                (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who I Help Section */}
      <section className="py-16 bg-white" data-testid="who-i-help-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Who I Help
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Whether you're responding to an emergency, maintaining our highways, or keeping public infrastructure running, you face unique on-the-job risks. If a negligent driver or third party causes your injury, you deserve full compensation—not just workers' comp.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* Law Enforcement */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 hover-lift">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-lg font-bold text-secondary mb-3">Law Enforcement</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>Police officers</li>
                <li>Deputies</li>
                <li>State troopers</li>
                <li>Corrections officers</li>
              </ul>
            </div>

            {/* Fire & Rescue */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200 hover-lift">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12.9l-2.13 2.09c-.56.56-.87 1.29-.87 2.07C9 18.68 10.35 20 12 20s3-1.32 3-2.94c0-.78-.31-1.52-.87-2.07L12 12.9zm0-8.39l-4.6 4.6c-1.18 1.18-1.84 2.79-1.84 4.46 0 3.52 2.87 6.39 6.44 6.39s6.44-2.87 6.44-6.39c0-1.67-.66-3.28-1.84-4.46L12 4.51z"/>
                </svg>
              </div>
              <h3 className="font-heading text-lg font-bold text-secondary mb-3">Fire & Rescue</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>Firefighters</li>
                <li>Fire marshals</li>
                <li>Wildland firefighters</li>
                <li>Fire rescue teams</li>
              </ul>
            </div>

            {/* Emergency Medical */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200 hover-lift">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-lg font-bold text-secondary mb-3">Emergency Medical</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>Paramedics</li>
                <li>EMTs</li>
                <li>Emergency dispatchers</li>
                <li>Medical transport</li>
              </ul>
            </div>

            {/* Transportation Workers */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-6 border border-amber-200 hover-lift">
              <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="font-heading text-lg font-bold text-secondary mb-3">Transportation</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>WSDOT workers</li>
                <li>Highway maintenance</li>
                <li>Traffic control</li>
                <li>Flaggers</li>
              </ul>
            </div>

            {/* Public Works */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-6 border border-slate-300 hover-lift">
              <div className="w-12 h-12 bg-slate-600 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-heading text-lg font-bold text-secondary mb-3">Public Works</h3>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>County road crews</li>
                <li>City maintenance</li>
                <li>Utility workers</li>
                <li>Infrastructure teams</li>
              </ul>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-slate-600 mb-4">
              <strong className="text-secondary">The common thread:</strong> If someone else's negligence caused your on-the-job injury, you likely have a civil claim beyond workers' compensation.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 text-ocean-600 font-semibold hover:text-ocean-700"
            >
              Contact me for a free consultation
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Pursue a Claim */}
      <section className="py-20 bg-slate-50" data-testid="why-pursue-claim">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Why Pursue a Civil Claim?
            </h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              Pursuing a civil claim as a public safety worker injured on the job offers critical advantages beyond workers' compensation alone. While workers' comp provides essential benefits like medical care and wage replacement, it often falls short in addressing the full scope of long-term impacts and financial burdens.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <DollarSign className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Workers' Compensation May Not Cover All Expenses
              </h3>
              <p className="text-slate-600 leading-relaxed">
                In Washington State, benefits for peace officers include presumptions for certain conditions like PTSD from traumatic events under <a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=51.32.185" target="_blank" rel="noopener noreferrer" className="text-ocean-600 underline hover:text-ocean-800">RCW 51.32.185</a>, but limitations exist—such as caps on wage replacement (often 60-75% of earnings) and no coverage for non-economic losses. A lawsuit supplements this by targeting the criminal's assets or insurance for uncovered costs, like extended therapy or adaptive equipment.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <Scale className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                No Upfront Costs
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Claims proceed on a contingency fee basis, meaning legal fees are only paid upon a successful outcome, reducing financial risk during recovery. Upon a successful outcome for the plaintiff, I deduct case fees and a predetermined percentage from your award.
              </p>
              <Link to="/pricing" className="inline-flex items-center gap-1 text-ocean-600 font-medium hover:text-ocean-700">
                View full pricing details <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <Award className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Full Compensation
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Workers' compensation covers medical expenses and lost wages but typically excludes pain, suffering, and emotional distress. Lawsuits allow recovery for these, which are common in high-stress pursuits leading to injuries like traumatic brain injury (TBI), spinal cord damage, or whiplash.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <Heart className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Addressing Unknown Medical Issues Down the Line
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Many injuries from car crashes in pursuits do not appear immediately but can emerge months or years later, such as chronic back pain, post-traumatic stress disorder (PTSD), or neurological complications. Workers' compensation may not fully accommodate these future needs, whereas a successful claim can provide funds for ongoing treatment and rehabilitation.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 hover-lift">
              <FileText className="w-10 h-10 text-ocean-600 mb-6" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                Legal Basis
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Being a peace officer does not mean that you forfeit your legal protections or remedies. For example, in pursuits, if a criminal's reckless or intentional behavior causes harm, officers can file tort claims for negligence or assault. Immunity protections for officers do not bar them from suing; instead, they limit criminal defenses.
              </p>
            </div>

            <div className="bg-ocean-600 rounded-2xl p-8 text-white">
              <Shield className="w-10 h-10 text-ocean-300 mb-6" />
              <h3 className="font-heading text-xl font-semibold mb-4">
                Your Rights Matter
              </h3>
              <p className="text-ocean-100 leading-relaxed">
                Don't assume you have no options beyond workers' comp. As a peace officer, you have the right to pursue full compensation for your injuries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Case Example */}
      <section className="py-20 bg-ocean-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-10 shadow-xl border-l-4 border-ocean-500">
              <span className="inline-block bg-ocean-100 text-ocean-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Real Case Example
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-6">
                Six-Figure Settlement for On-Duty Peace Officer
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                A peace officer engaged in a pursuit with a fleeing driver attempted a lawful intervention technique to stop the vehicle and was then rammed by the suspect, resulting in a collision and injuries to the peace officer. Despite assumptions of workers' comp exclusivity, <strong>I secured a six-figure settlement from the criminal's motor vehicle insurer</strong>, covering damages beyond standard benefits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Amy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Why Choose Amy C. Brown Law?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                If you're a peace officer injured in a work-related crash, Amy C. Brown Law is uniquely equipped to handle your case. With over ten years of experience, including a successful six-figure settlement for an officer injured during a lawful pursuit, I understand the complexities of suing criminals and their insurers.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                My defense background provides strategic insight into countering insurance tactics, ensuring robust evidence handling to maximize your recovery. Unlike large firms, I offer personalized attention, working directly with you to navigate legal complexities while you focus on healing.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Operating on a contingency fee basis, there are no upfront costs, and my commitment to accountability aligns with your pursuit of justice.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Clock className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">10+ Years Experience</h4>
                <p className="text-sm text-slate-600">Proven track record with first responder cases</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Award className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Six-Figure Results</h4>
                <p className="text-sm text-slate-600">Successful settlements for officers</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Shield className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Defense Insight</h4>
                <p className="text-sm text-slate-600">I know insurance company tactics</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Users className="w-10 h-10 text-ocean-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Personal Attention</h4>
                <p className="text-sm text-slate-600">Work directly with me, not a paralegal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statute of Limitations */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <AlertCircle className="w-16 h-16 text-ocean-400 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            File Your Claim On Time
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-8">
            In Washington State, the <a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=4.16.080" target="_blank" rel="noopener noreferrer" className="text-ocean-300 underline hover:text-white font-medium">statute of limitations</a> for most personal injury claims is three years, however, there are exceptions. For this reason, if you've been hurt under any circumstance, you should reach out to me sooner, rather than later.
          </p>
          <a 
            href="tel:425-675-1224"
            className="inline-flex items-center gap-3 bg-ocean-500 text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-ocean-600 transition-all duration-300"
            data-testid="first-responder-limitations-cta"
          >
            <Phone className="w-6 h-6" />
            Contact me today at (425) 675-1224
          </a>
        </div>
      </section>

      {/* Helpful Resources - Internal Linking */}
      <section className="py-16 bg-slate-50" data-testid="first-responder-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-ocean-100 text-ocean-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Learn More
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Resources by Profession
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Detailed guides for your specific role — third-party claims, L&I coordination, and what to do after an injury.
            </p>
          </div>
          
          {/* Sector-specific blog posts */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <Link
              to="/blog/firefighter-injury-claims-washington"
              className="group bg-white hover:bg-red-50 rounded-xl p-5 border border-slate-200 hover:border-red-300 transition-all duration-300 shadow-sm"
            >
              <Flame className="w-8 h-8 text-red-600 mb-3" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-red-700 transition-colors text-sm">
                Firefighter Injuries
              </h3>
              <p className="text-slate-600 text-xs mb-2">
                Third-party claims for fire & rescue workers injured on duty.
              </p>
              <span className="inline-flex items-center gap-1 text-red-600 font-medium text-xs group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            
            <Link
              to="/blog/paramedic-emt-injury-claims-washington"
              className="group bg-white hover:bg-emerald-50 rounded-xl p-5 border border-slate-200 hover:border-emerald-300 transition-all duration-300 shadow-sm"
            >
              <Heart className="w-8 h-8 text-emerald-600 mb-3" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-emerald-700 transition-colors text-sm">
                Paramedic & EMT Injuries
              </h3>
              <p className="text-slate-600 text-xs mb-2">
                Rights for EMS workers when third-party negligence causes harm.
              </p>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-medium text-xs group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            
            <Link
              to="/blog/wsdot-highway-worker-injury-claims-washington"
              className="group bg-white hover:bg-amber-50 rounded-xl p-5 border border-slate-200 hover:border-amber-300 transition-all duration-300 shadow-sm"
            >
              <HardHat className="w-8 h-8 text-amber-600 mb-3" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-amber-700 transition-colors text-sm">
                WSDOT & Highway Workers
              </h3>
              <p className="text-slate-600 text-xs mb-2">
                Civil claims against negligent drivers in work zones.
              </p>
              <span className="inline-flex items-center gap-1 text-amber-600 font-medium text-xs group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            
            <Link
              to="/blog/public-works-employee-injury-claims-washington"
              className="group bg-white hover:bg-slate-100 rounded-xl p-5 border border-slate-200 hover:border-slate-400 transition-all duration-300 shadow-sm"
            >
              <Wrench className="w-8 h-8 text-slate-600 mb-3" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-slate-700 transition-colors text-sm">
                Public Works Employees
              </h3>
              <p className="text-slate-600 text-xs mb-2">
                County road crews, city maintenance, and utility workers.
              </p>
              <span className="inline-flex items-center gap-1 text-slate-600 font-medium text-xs group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
          
          {/* General resources */}
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/first-responder-injury-rights-washington"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Shield className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                First Responder Injury Rights
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Learn about civil claims, third-party liability, and options beyond workers' comp in Washington.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/blog/why-statutes-of-limitations-exist-washington-personal-injury"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Clock className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                Filing Deadlines & Time Limits
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Washington's statute of limitations explained—don't miss your window to file a claim.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/compensation-estimator"
              className="group bg-white hover:bg-ocean-50 rounded-xl p-6 border border-slate-200 hover:border-ocean-300 transition-all duration-300 shadow-sm"
            >
              <Calculator className="w-10 h-10 text-ocean-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-ocean-700 transition-colors">
                What Is My Case Worth?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Use our free calculator to estimate your potential compensation for your injuries.
              </p>
              <span className="inline-flex items-center gap-1 text-ocean-600 font-medium text-sm group-hover:gap-2 transition-all">
                Try Calculator <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FirstResponderFAQ
        title="Public Safety Worker Injury FAQs"
        subtitle="Common questions about injury claims for police, firefighters, EMS, WSDOT workers, and public works employees."
        faqs={firstResponderFAQs}
        schemaId="first-responder"
      />

      {/* Video Resources Section */}
      <PageVideoSection
        videos={firstResponderVideos}
        title="First Responder Injury Videos"
        subtitle="Learn how third-party claims work alongside workers' compensation."
        theme="teal"
      />

      {/* Related Practice Areas */}
      <RelatedPracticeAreas currentSlug="first-responder-crashes" />

      {/* CTA */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation - No Obligation
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex flex-col items-center justify-center gap-1 bg-white text-secondary px-10 py-5 rounded-full font-bold text-xl hover:bg-slate-100 transition-all duration-300 shadow-xl"
              data-testid="first-responder-cta-call"
            >
              <span className="flex items-center gap-2">
                <Phone className="w-6 h-6" />
                Call
              </span>
              <span>(425) 675-1224</span>
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex flex-col items-center justify-center gap-1 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="w-6 h-6" />
                Text
              </span>
              <span>(425) 675-1224</span>
            </a>
          </div>
          <p className="mt-6 text-slate-400 text-sm">
            Page last updated: February 2026
          </p>
        </div>
      </section>
    </div>
  );
};

export default FirstResponderPage;
